import React, { useState, useEffect } from 'react';

export default function ReportSectionsPanel({ sections, onEdit, onDelete, onExportSections, isExporting }) {
  const [checkedIds, setCheckedIds] = useState(() => new Set(sections.map(s => s.id)));
  const [collapsed, setCollapsed] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  // Keep checked set in sync as sections list changes
  useEffect(() => {
    setCheckedIds(prev => {
      const next = new Set(prev);
      // Auto-check new sections
      sections.forEach(s => { if (!next.has(s.id)) next.add(s.id); });
      // Remove stale IDs
      for (const id of next) {
        if (!sections.find(s => s.id === id)) next.delete(id);
      }
      return next;
    });
  }, [sections]);

  const toggleCheck = (id) => {
    setCheckedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const checkedSections = sections.filter(s => checkedIds.has(s.id));

  const handleDelete = async (section) => {
    setDeletingId(section.id);
    try { await onDelete(section); } finally { setDeletingId(null); }
  };

  if (sections.length === 0) return null;

  return (
    <div style={{
      background: 'var(--cc-card-bg)',
      borderRadius: 12,
      border: '2px solid var(--branding-app-primary)',
      marginBottom: 24,
      overflow: 'hidden',
    }}>
      {/* Panel header */}
      <div
        onClick={() => setCollapsed(v => !v)}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '13px 18px',
          background: 'var(--branding-app-surface-panel)',
          borderBottom: collapsed ? 'none' : '1px solid var(--branding-app-surface-panel-border)',
          cursor: 'pointer', userSelect: 'none',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--branding-app-primary)" strokeWidth="2.5">
            <path d="M9 12h6M9 16h6M9 8h6M5 3h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z"/>
          </svg>
          <span style={{ fontWeight: 700, fontSize: 14, color: 'var(--cc-text-primary)' }}>
            Report sections
          </span>
          <span style={{
            background: 'var(--branding-app-primary)', color: 'var(--branding-app-primary-fg)',
            borderRadius: 20, padding: '1px 9px', fontSize: 12, fontWeight: 700,
          }}>
            {sections.length}
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {!collapsed && (
            <button
              type="button"
              onClick={e => { e.stopPropagation(); onExportSections(checkedSections); }}
              disabled={checkedSections.length === 0 || isExporting}
              style={{
                background: checkedSections.length > 0 && !isExporting ? 'var(--branding-app-primary)' : '#cbd5e1',
                color: checkedSections.length > 0 && !isExporting ? 'var(--branding-app-primary-fg)' : '#64748b',
                border: 'none', borderRadius: 7, padding: '6px 14px',
                fontWeight: 600, fontSize: 13,
                cursor: checkedSections.length === 0 || isExporting ? 'not-allowed' : 'pointer',
              }}
            >
              {isExporting
                ? 'Generating…'
                : `Export section${checkedSections.length !== 1 ? 's' : ''} PDF${checkedSections.length < sections.length ? ` (${checkedSections.length})` : ''}`}
            </button>
          )}
          <svg
            width="16" height="16" viewBox="0 0 24 24" fill="none"
            stroke="var(--cc-text-muted)" strokeWidth="2"
            style={{ transform: collapsed ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}
          >
            <polyline points="18 15 12 9 6 15"/>
          </svg>
        </div>
      </div>

      {!collapsed && (
        <div style={{ padding: '12px 18px 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
          {sections.map((section, idx) => {
            const checked = checkedIds.has(section.id);
            return (
              <div
                key={section.id}
                style={{
                  display: 'flex', alignItems: 'flex-start', gap: 12,
                  padding: '12px 14px',
                  background: checked ? 'var(--branding-app-surface-panel)' : 'var(--cc-card-bg)',
                  border: `1px solid ${checked ? 'var(--branding-app-primary)' : 'var(--branding-app-surface-panel-border)'}`,
                  borderRadius: 8,
                  transition: 'all 0.15s ease',
                }}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggleCheck(section.id)}
                  style={{ marginTop: 3, width: 16, height: 16, cursor: 'pointer', flexShrink: 0 }}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--cc-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      §{idx + 1}
                    </span>
                    <span style={{ fontWeight: 700, fontSize: 14, color: 'var(--cc-text-primary)' }}>
                      {section.name}
                    </span>
                    <span style={{ fontSize: 12, color: 'var(--cc-text-muted)' }}>
                      {(section.itemIds?.length ?? 0)} item{section.itemIds?.length !== 1 ? 's' : ''}
                    </span>
                  </div>
                  {section.annotation ? (
                    <p style={{
                      margin: '5px 0 0',
                      fontSize: 13, color: 'var(--cc-text-secondary)',
                      fontStyle: 'italic', lineHeight: 1.45,
                      overflow: 'hidden',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                    }}>
                      {section.annotation}
                    </p>
                  ) : (
                    <p style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--cc-text-muted)', fontStyle: 'italic' }}>
                      No annotation
                    </p>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                  <button
                    type="button"
                    onClick={() => onEdit(section)}
                    style={{
                      background: 'var(--cc-cancel-bg)', color: 'var(--cc-text-primary)',
                      border: 'none', borderRadius: 6, padding: '5px 11px',
                      fontSize: 12, fontWeight: 500, cursor: 'pointer',
                    }}
                  >Edit</button>
                  <button
                    type="button"
                    onClick={() => handleDelete(section)}
                    disabled={deletingId === section.id}
                    style={{
                      background: '#fee2e2', color: '#dc2626',
                      border: 'none', borderRadius: 6, padding: '5px 11px',
                      fontSize: 12, fontWeight: 600,
                      cursor: deletingId === section.id ? 'not-allowed' : 'pointer',
                      opacity: deletingId === section.id ? 0.6 : 1,
                    }}
                  >
                    {deletingId === section.id ? '…' : 'Delete'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
